'use client'

import { useCallback, useState } from 'react'
import { Copy, Download } from 'lucide-react'

interface MoverRow {
  symbol:        string
  sec_type:      string | null
  pct_change_7d: number
  price_now:     number
  price_7d_ago:  number
  change_7d:     number
  max_rvol:      number
  alert_count:   number
}

const SEC_TYPE_LABEL: Record<string, string> = {
  ETF: 'ETF', ADRC: 'ADR', PFD: 'Pref', WARRANT: 'Warr',
  SP: 'Bond', ETS: 'ETS', ETV: 'ETV', ETN: 'ETN',
  FUND: 'Fund', UNIT: 'Unit', RIGHT: 'Right', GDR: 'GDR', NYRS: 'NYRS',
}
const secTypeLabel = (t: string | null) => (!t || t === 'CS' || t === 'OS') ? null : (SEC_TYPE_LABEL[t] ?? t)

// Match dashboard helpers exactly
const fmtPct   = (n: number) => `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`
const pctColor = (n: number) => n > 0 ? '#34d399' : n < 0 ? '#f87171' : '#71717a'

function fmtPrice(n: number) {
  if (!Number.isFinite(n) || n === 0) return '—'
  if (n < 0.0001) return `$${n.toFixed(6)}`
  if (n < 0.01)   return `$${n.toFixed(4)}`
  if (n < 1)      return `$${n.toFixed(3)}`
  if (n >= 10000) return `$${Math.round(n).toLocaleString('en-US')}`
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 })
}
function fmtDelta(n: number) {
  if (!Number.isFinite(n)) return '—'
  const abs = Math.abs(n), sign = n >= 0 ? '+' : '-'
  if (abs < 0.0001) return `${sign}$${abs.toFixed(6)}`
  if (abs < 0.01)   return `${sign}$${abs.toFixed(4)}`
  if (abs < 1)      return `${sign}$${abs.toFixed(3)}`
  if (abs >= 10000) return `${sign}$${Math.round(abs).toLocaleString('en-US')}`
  return `${sign}${abs.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 })}`
}

function downloadDataUrl(url: string, name: string) {
  const a = document.createElement('a')
  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove()
}
function loadImg(src: string, ms = 5000): Promise<HTMLImageElement | null> {
  return new Promise(resolve => {
    const img = new Image(); img.crossOrigin = 'anonymous'
    const t = setTimeout(() => resolve(null), ms)
    img.onload  = () => { clearTimeout(t); resolve(img) }
    img.onerror = () => { clearTimeout(t); resolve(null) }
    img.src = src
  })
}
function rr(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  r = Math.min(r, w / 2, h / 2)
  ctx.beginPath()
  ctx.moveTo(x + r, y); ctx.lineTo(x + w - r, y)
  ctx.quadraticCurveTo(x + w, y, x + w, y + r)
  ctx.lineTo(x + w, y + h - r)
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h)
  ctx.lineTo(x + r, y + h)
  ctx.quadraticCurveTo(x, y + h, x, y + h - r)
  ctx.lineTo(x, y + r)
  ctx.quadraticCurveTo(x, y, x + r, y)
  ctx.closePath()
}

function generateMoversText(rows: MoverRow[], generatedAt: string | undefined): string {
  const top20 = rows.slice()
    .sort((a, b) => Math.abs(b.pct_change_7d) - Math.abs(a.pct_change_7d))
    .slice(0, 20)
  const gainers = rows.filter(r => r.pct_change_7d > 0).length
  const losers  = rows.filter(r => r.pct_change_7d < 0).length
  const topGain = [...rows].sort((a, b) => b.pct_change_7d - a.pct_change_7d)[0]
  const topLoss = [...rows].sort((a, b) => a.pct_change_7d - b.pct_change_7d)[0]

  const ds = new Date(generatedAt ?? Date.now()).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/New_York',
  })

  const typeStr = (t: string | null) => (!t || t === 'CS' || t === 'OS') ? '' : ` (${t})`

  const lines: string[] = []
  lines.push('## TheDesperateTrader.com | 7-Day Top Movers')
  lines.push(`*${ds} | ${rows.length.toLocaleString()} symbols scanned | ${gainers} ▲ gainers | ${losers} ▼ losers*`)
  lines.push('')
  if (topGain && topLoss) {
    lines.push(`**Biggest Gainer:** ${topGain.symbol}${typeStr(topGain.sec_type)} ${fmtPct(topGain.pct_change_7d)} | **Biggest Loser:** ${topLoss.symbol}${typeStr(topLoss.sec_type)} ${fmtPct(topLoss.pct_change_7d)}`)
  }
  lines.push('')
  lines.push('---')
  lines.push('')
  lines.push('| # | Symbol | Price | 7D Chg | 7D % | Alerts | Peak RVOL |')
  lines.push('|---|--------|-------|--------|------|--------|-----------|')

  for (let i = 0; i < top20.length; i++) {
    const r = top20[i]
    const trend = r.pct_change_7d >= 0 ? '▲' : '▼'
    const rv = r.max_rvol
    const rvStr = rv <= 0 ? '—'
      : rv >= 10000 ? `${(rv / 1000).toFixed(0)}k×`
      : rv >= 1000  ? `${(rv / 1000).toFixed(1)}k×`
      : rv >= 100   ? `${rv.toFixed(0)}×`
      : `${rv.toFixed(1)}×`
    const ac = r.alert_count ?? 0
    const acStr = ac >= 10000 ? `${(ac / 1000).toFixed(0)}k` : ac >= 1000 ? `${(ac / 1000).toFixed(1)}k` : `${ac}`
    lines.push(`| ${i + 1} | ${trend} ${r.symbol}${typeStr(r.sec_type)} | ${fmtPrice(r.price_now)} | ${fmtDelta(r.change_7d)} | ${fmtPct(r.pct_change_7d)} | ${acStr} | ${rvStr} |`)
  }

  lines.push('')
  lines.push('---')
  lines.push('*Real-time RVOL scanner · 11,000+ symbols · thedesperatetrader.com*')
  return lines.join('\n')
}

async function renderCard(rows: MoverRow[], generatedAt: string | undefined): Promise<string> {
  await document.fonts.ready

  const W = 1080, H = 1350, S = 2
  const PAD = 44, CW = W - PAD * 2   // 992px
  const EDGE = W - PAD               // 1036px

  const canvas = document.createElement('canvas')
  canvas.width = W * S; canvas.height = H * S
  const ctx = canvas.getContext('2d')!
  ctx.scale(S, S)

  // ── BACKGROUND: match app's zinc-950 (#09090b) ────────────────────────────
  ctx.fillStyle = '#09090b'; ctx.fillRect(0, 0, W, H)

  // Subtle ambient glows — same vibe as app, not overpowering
  const ga = ctx.createRadialGradient(0, 0, 0, 0, 0, 640)
  ga.addColorStop(0, 'rgba(245,158,11,0.18)'); ga.addColorStop(1, 'transparent')
  ctx.fillStyle = ga; ctx.fillRect(0, 0, W, H)

  const gs = ctx.createRadialGradient(W, 0, 0, W, 0, 540)
  gs.addColorStop(0, 'rgba(56,189,248,0.10)'); gs.addColorStop(1, 'transparent')
  ctx.fillStyle = gs; ctx.fillRect(0, 0, W, H)

  // Edge vignette
  const gv = ctx.createRadialGradient(W/2, H/2, W*0.35, W/2, H/2, W)
  gv.addColorStop(0, 'rgba(0,0,0,0)'); gv.addColorStop(1, 'rgba(0,0,0,0.50)')
  ctx.fillStyle = gv; ctx.fillRect(0, 0, W, H)

  // Amber top-stripe (brand signature)
  const gt = ctx.createLinearGradient(0, 0, W, 0)
  gt.addColorStop(0, 'transparent'); gt.addColorStop(0.1, 'rgba(245,158,11,0.95)')
  gt.addColorStop(0.9, 'rgba(245,158,11,0.95)'); gt.addColorStop(1, 'transparent')
  ctx.fillStyle = gt; ctx.fillRect(0, 0, W, 3)

  const logoImg = await loadImg('/assets/logo/thedesperatetrader-logo.svg')

  let y = PAD

  // ── BRANDING ─────────────────────────────────────────────────────────────────
  const LOGO_SZ = 40, brandCY = y + LOGO_SZ / 2
  if (logoImg) {
    ctx.drawImage(logoImg, PAD, y, LOGO_SZ, LOGO_SZ)
  } else {
    rr(ctx, PAD, y, LOGO_SZ, LOGO_SZ, 9)
    const fb = ctx.createLinearGradient(PAD, y, PAD+LOGO_SZ, y+LOGO_SZ)
    fb.addColorStop(0, '#f59e0b'); fb.addColorStop(1, '#d97706')
    ctx.fillStyle = fb; ctx.fill()
    ctx.font = '700 15px Inter, ui-sans-serif'; ctx.fillStyle = '#000'
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
    ctx.fillText('DT', PAD+LOGO_SZ/2, brandCY)
  }
  const TX = PAD + LOGO_SZ + 12
  ctx.font = '800 26px Inter, ui-sans-serif'
  ctx.textAlign = 'left'; ctx.textBaseline = 'middle'; ctx.fillStyle = '#f4f4f5'
  ctx.fillText('TheDesperateTrader', TX, brandCY)
  const tdtW = ctx.measureText('TheDesperateTrader').width
  ctx.fillStyle = '#f59e0b'; ctx.fillText('.com', TX + tdtW, brandCY)

  // Date pill
  const ds = new Date(generatedAt ?? Date.now()).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/New_York',
  })
  ctx.font = '500 12px Inter, ui-sans-serif'
  const dw = ctx.measureText(ds).width
  const pW = dw + 26, pH = 30, pX = EDGE - pW, pY = brandCY - pH/2
  rr(ctx, pX, pY, pW, pH, 8)
  ctx.fillStyle = 'rgba(24,24,27,0.90)'; ctx.fill()
  ctx.strokeStyle = 'rgba(63,63,70,0.80)'; ctx.lineWidth = 1; ctx.stroke()
  ctx.fillStyle = '#71717a'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
  ctx.fillText(ds, pX + pW/2, brandCY)

  y += LOGO_SZ + 18

  // ── TITLE ─────────────────────────────────────────────────────────────────────
  ctx.font = '800 64px Inter, ui-sans-serif'
  ctx.fillStyle = '#f4f4f5'; ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic'
  ctx.fillText('7-Day Top Movers', PAD, y + 58)
  y += 72

  // ── SUBTITLE ──────────────────────────────────────────────────────────────────
  const gainers = rows.filter(r => r.pct_change_7d > 0).length
  const losers  = rows.filter(r => r.pct_change_7d < 0).length
  let sx = PAD
  for (const p of [
    { t: `${rows.length.toLocaleString()} symbols scanned`, c: '#71717a', w: '400' },
    { t: `   ▲ ${gainers} gainers`, c: '#34d399', w: '600' },
    { t: `   ▼ ${losers} losers`,  c: '#f87171', w: '600' },
  ]) {
    ctx.font = `${p.w} 17px Inter, ui-sans-serif`
    ctx.fillStyle = p.c; ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic'
    ctx.fillText(p.t, sx, y + 16); sx += ctx.measureText(p.t).width
  }
  y += 30

  const divider = () => {
    ctx.beginPath(); ctx.moveTo(PAD, y); ctx.lineTo(EDGE, y)
    ctx.strokeStyle = 'rgba(63,63,70,0.50)'; ctx.lineWidth = 1; ctx.stroke()
  }
  divider(); y += 14

  // ── STAT CARDS ────────────────────────────────────────────────────────────────
  const topGain = [...rows].sort((a, b) => b.pct_change_7d - a.pct_change_7d)[0]
  const topLoss = [...rows].sort((a, b) => a.pct_change_7d - b.pct_change_7d)[0]
  const CH = 90, CG = 8, CWi = (CW - CG * 4) / 5
  const SCARD = [
    { lbl: 'Total Symbols',  val: `${rows.length}`, sub: 'with 7-day data',                                                          vc: '#f4f4f5' },
    { lbl: 'Gainers',        val: `${gainers}`,     sub: rows.length ? `${((gainers/rows.length)*100).toFixed(0)}% of universe` : '', vc: '#34d399' },
    { lbl: 'Losers',         val: `${losers}`,      sub: rows.length ? `${((losers/rows.length)*100).toFixed(0)}% of universe`  : '', vc: '#f87171' },
    { lbl: 'Biggest Gainer', val: topGain ? fmtPct(topGain.pct_change_7d) : '—', sub: topGain?.symbol ?? '', vc: '#34d399' },
    { lbl: 'Biggest Loser',  val: topLoss ? fmtPct(topLoss.pct_change_7d) : '—', sub: topLoss?.symbol ?? '', vc: '#f87171' },
  ]
  for (let i = 0; i < SCARD.length; i++) {
    const c = SCARD[i], cx = PAD + i * (CWi + CG), cy = y
    rr(ctx, cx, cy, CWi, CH, 12)
    ctx.fillStyle = 'rgba(24,24,27,0.60)'; ctx.fill()
    ctx.strokeStyle = 'rgba(63,63,70,0.80)'; ctx.lineWidth = 1; ctx.stroke()
    ctx.font = '600 11px Inter, ui-sans-serif'
    ctx.fillStyle = '#71717a'; ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic'
    ctx.fillText(c.lbl.toUpperCase(), cx + 14, cy + 20)
    const vf = c.val.length > 9 ? '18px' : c.val.length > 6 ? '22px' : '28px'
    ctx.font = `700 ${vf} Inter, ui-sans-serif`; ctx.fillStyle = c.vc
    ctx.fillText(c.val, cx + 14, cy + 58)
    if (c.sub) { ctx.font = '400 12px Inter, ui-sans-serif'; ctx.fillStyle = '#52525b'; ctx.fillText(c.sub, cx + 14, cy + 76) }
  }
  y += CH + 14; divider(); y += 12

  // ── TABLE — top 10 rows, large fonts for social media readability ──────────
  const top10   = rows.slice().sort((a, b) => Math.abs(b.pct_change_7d) - Math.abs(a.pct_change_7d)).slice(0, 10)
  const ROW_H   = 80, ROW_G = 8
  // Right-anchored columns
  const RVOL_R  = EDGE - 14          // 1022
  const ALRT_R  = RVOL_R  - 92 - 14  // 916
  const PCT_R   = ALRT_R  - 88 - 14  // 814
  const BAR_R   = PCT_R   - 110 - 10  // 694
  const BAR_W   = 120
  const BAR_X   = BAR_R   - BAR_W   // 574
  // Left-anchored columns
  const RNK_X   = PAD + 4            // 48
  const SYM_X   = PAD + 52           // 96
  const PRC_X   = SYM_X + 200        // 296
  const DLT_X   = PRC_X + 120 + 14   // 430

  // ── COLUMN HEADERS ──────────────────────────────────────────────────────────
  ctx.font = '600 12px Inter, ui-sans-serif'; ctx.fillStyle = '#52525b'
  ctx.textAlign = 'left';  ctx.textBaseline = 'alphabetic'
  ctx.fillText('#',        RNK_X,  y + 13)
  ctx.fillText('Symbol',   SYM_X,  y + 13)
  ctx.fillText('Price',    PRC_X,  y + 13)
  ctx.fillText('7D Chg $', DLT_X,  y + 13)
  ctx.textAlign = 'right'
  ctx.fillText('7D Chg %', PCT_R,  y + 13)
  ctx.fillText('Alerts',   ALRT_R, y + 13)
  ctx.fillText('Peak RVOL',RVOL_R, y + 13)
  y += 22

  // Header separator
  ctx.beginPath(); ctx.moveTo(PAD, y); ctx.lineTo(EDGE, y)
  ctx.strokeStyle = 'rgba(63,63,70,0.35)'; ctx.lineWidth = 1; ctx.stroke()
  y += 8

  // ── DATA ROWS ────────────────────────────────────────────────────────────────
  for (let i = 0; i < top10.length; i++) {
    const row    = top10[i]
    const isGain = row.pct_change_7d >= 0
    const absPct = Math.abs(row.pct_change_7d)
    const isHot  = absPct >= 30
    const ry     = y + i * (ROW_H + ROW_G)
    const rCY    = ry + ROW_H / 2

    // Row background
    if (isHot) {
      ctx.fillStyle = 'rgba(245,158,11,0.06)'; ctx.fillRect(PAD, ry, CW, ROW_H)
    } else if (i % 2 === 0) {
      ctx.fillStyle = 'rgba(24,24,27,0.30)'; ctx.fillRect(PAD, ry, CW, ROW_H)
    }

    // Row separator
    ctx.beginPath(); ctx.moveTo(PAD, ry + ROW_H); ctx.lineTo(EDGE, ry + ROW_H)
    ctx.strokeStyle = 'rgba(63,63,70,0.30)'; ctx.lineWidth = 1; ctx.stroke()

    // ── RANK ─────────────────────────────────────────────────────────────────
    const rankColor = i === 0 ? '#f59e0b' : i === 1 ? '#d4d4d8' : i === 2 ? '#b45309' : '#52525b'
    ctx.font = `700 ${i < 9 ? 16 : 14}px Inter, ui-sans-serif`
    ctx.fillStyle = rankColor; ctx.textAlign = 'right'; ctx.textBaseline = 'middle'
    ctx.fillText(String(i + 1), RNK_X + 30, rCY)

    // ── SYMBOL ───────────────────────────────────────────────────────────────
    ctx.font = '13px Inter, ui-sans-serif'
    ctx.fillStyle = isGain ? '#10b981' : '#f87171'
    ctx.textAlign = 'left'; ctx.textBaseline = 'middle'
    ctx.fillText(isGain ? '▲' : '▼', SYM_X, rCY)

    ctx.font = '700 18px ui-monospace, Consolas, Menlo, monospace'
    ctx.fillStyle = '#f4f4f5'
    ctx.fillText(row.symbol, SYM_X + 18, rCY)
    const symW = ctx.measureText(row.symbol).width

    // Type badge
    const lbl = secTypeLabel(row.sec_type)
    let badgeEnd = SYM_X + 18 + symW
    if (lbl) {
      const lx = badgeEnd + 5
      ctx.font = '500 10px Inter, ui-sans-serif'
      const lw = ctx.measureText(lbl).width
      rr(ctx, lx, rCY - 9, lw + 10, 18, 4)
      ctx.fillStyle = 'rgba(39,39,42,1)'; ctx.fill()
      ctx.strokeStyle = 'rgba(63,63,70,0.70)'; ctx.stroke()
      ctx.fillStyle = '#71717a'; ctx.textAlign = 'left'; ctx.textBaseline = 'middle'
      ctx.fillText(lbl, lx + 5, rCY)
      badgeEnd = lx + lw + 10
    }

    // HOT badge
    if (isHot) {
      const hx = badgeEnd + 6
      ctx.font = '700 10px Inter, ui-sans-serif'
      const hw = ctx.measureText('HOT').width
      rr(ctx, hx, rCY - 9, hw + 10, 18, 4)
      ctx.fillStyle = 'rgba(245,158,11,0.20)'; ctx.fill()
      ctx.strokeStyle = 'rgba(245,158,11,0.30)'; ctx.stroke()
      ctx.fillStyle = '#f59e0b'; ctx.textAlign = 'left'; ctx.textBaseline = 'middle'
      ctx.fillText('HOT', hx + 5, rCY)
    }

    // ── PRICE — two lines ─────────────────────────────────────────────────────
    ctx.font = '600 16px ui-monospace, Consolas, Menlo, monospace'
    ctx.fillStyle = '#e4e4e7'; ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic'
    ctx.fillText(fmtPrice(row.price_now), PRC_X, ry + 28)
    ctx.font = '400 13px ui-monospace, Consolas, Menlo, monospace'
    ctx.fillStyle = '#52525b'
    ctx.fillText(`7d: ${fmtPrice(row.price_7d_ago)}`, PRC_X, ry + 50)

    // ── 7D CHANGE $ ──────────────────────────────────────────────────────────
    ctx.font = '600 16px ui-monospace, Consolas, Menlo, monospace'
    ctx.fillStyle = pctColor(row.change_7d)
    ctx.textAlign = 'left'; ctx.textBaseline = 'middle'
    ctx.fillText(fmtDelta(row.change_7d), DLT_X, rCY)

    // ── BAR + 7D % ───────────────────────────────────────────────────────────
    rr(ctx, BAR_X, rCY - 4, BAR_W, 8, 4)
    ctx.fillStyle = '#27272a'; ctx.fill()
    const barFill = Math.min(absPct * 1.5, 100) / 100
    if (barFill > 0) {
      rr(ctx, BAR_X, rCY - 4, Math.max(8, barFill * BAR_W), 8, 4)
      ctx.fillStyle = isGain ? '#10b981' : '#f87171'; ctx.fill()
    }
    ctx.font = '700 17px ui-monospace, Consolas, Menlo, monospace'
    ctx.fillStyle = pctColor(row.pct_change_7d)
    ctx.textAlign = 'right'; ctx.textBaseline = 'middle'
    ctx.fillText(fmtPct(row.pct_change_7d), PCT_R, rCY)

    // ── ALERTS ───────────────────────────────────────────────────────────────
    const ac = row.alert_count ?? 0
    if (ac > 0) {
      const as2 = ac >= 10000 ? `${(ac/1000).toFixed(0)}k` : ac >= 1000 ? `${(ac/1000).toFixed(1)}k` : `${ac}`
      ctx.font = '600 14px Inter, ui-sans-serif'
      const aw = ctx.measureText(as2).width
      const alertX = ALRT_R - aw - 14, alertY = rCY - 12
      rr(ctx, alertX, alertY, aw + 14, 24, 6)
      ctx.fillStyle = '#27272a'; ctx.fill()
      ctx.fillStyle = '#d4d4d8'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
      ctx.fillText(as2, alertX + (aw + 14) / 2, rCY)
    }

    // ── RVOL ──────────────────────────────────────────────────────────────────
    const rv = row.max_rvol ?? 0
    if (rv > 0) {
      const rs = rv >= 10000 ? `${(rv/1000).toFixed(0)}k×` : rv >= 1000 ? `${(rv/1000).toFixed(1)}k×` : rv >= 100 ? `${rv.toFixed(0)}×` : `${rv.toFixed(1)}×`
      ctx.font = '600 15px ui-monospace, Consolas, Menlo, monospace'
      ctx.fillStyle = rv >= 10 ? '#f59e0b' : '#a1a1aa'
      ctx.textAlign = 'right'; ctx.textBaseline = 'middle'
      ctx.fillText(rs, RVOL_R, rCY)
    }
  }

  y += top10.length * (ROW_H + ROW_G) - ROW_G + 18
  divider(); y += 14

  // ── FOOTER ────────────────────────────────────────────────────────────────────
  ctx.font = '400 13px Inter, ui-sans-serif'
  ctx.fillStyle = '#3f3f46'; ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic'
  ctx.fillText('Real-time RVOL scanner · 11,000+ symbols', PAD, y + 15)

  // Footer brand — right-aligned
  ctx.font = '600 14px Inter, ui-sans-serif'
  const comW2  = ctx.measureText('.com').width
  const nameW2 = ctx.measureText('TheDesperateTrader').width
  if (logoImg) ctx.drawImage(logoImg, EDGE - comW2 - nameW2 - 8 - 20, y - 2, 20, 20)
  ctx.fillStyle = '#f59e0b'; ctx.textAlign = 'right'
  ctx.fillText('.com', EDGE, y + 15)
  ctx.fillStyle = '#71717a'
  ctx.fillText('TheDesperateTrader', EDGE - comW2, y + 15)

  return canvas.toDataURL('image/png', 1.0)
}

export default function WeeklyMoversSocialCard({
  rows, generatedAt,
}: {
  rows: MoverRow[]
  generatedAt?: string
}) {
  const [busy,   setBusy]   = useState(false)
  const [copied, setCopied] = useState(false)

  const handleDownload = useCallback(async () => {
    if (busy || rows.length === 0) return
    setBusy(true)
    try {
      const url = await renderCard(rows, generatedAt)
      const d = new Date()
      const ts = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
      downloadDataUrl(url, `weekly-movers-${ts}.png`)
    } finally { setBusy(false) }
  }, [busy, rows, generatedAt])

  const handleCopy = useCallback(() => {
    if (rows.length === 0) return
    const text = generateMoversText(rows, generatedAt)
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }).catch(console.error)
  }, [rows, generatedAt])

  return (
    <div className="inline-flex items-center gap-2">
      <button onClick={handleDownload} disabled={busy || rows.length === 0}
        className="inline-flex items-center gap-1.5 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-xs font-medium text-amber-300 hover:bg-amber-500/20 hover:border-amber-500/60 transition disabled:opacity-40 disabled:cursor-not-allowed"
        title="Download 1080×1350 social media image">
        <Download className="w-3.5 h-3.5" />
        {busy ? 'Generating…' : 'Share Image'}
      </button>
      <button onClick={handleCopy} disabled={rows.length === 0}
        className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-700/60 bg-zinc-800/60 px-3 py-1.5 text-xs font-medium text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700/60 hover:border-zinc-600/60 transition disabled:opacity-40 disabled:cursor-not-allowed"
        title="Copy formatted text for Reddit / social media">
        <Copy className="w-3.5 h-3.5" />
        {copied ? 'Copied!' : 'Copy Text'}
      </button>
    </div>
  )
}
